import type { JSONContent } from '@tiptap/core';
import {
  isAllowedHref,
  RICH_TEXT_MAX_DEPTH,
  type RichTextDocument,
  type RichTextInline,
  type RichTextListItem,
  type RichTextMark,
  type RichTextNode,
} from '@oscom/shared';

/**
 * Editor JSON -> stored format. TipTap adds attributes the API refuses
 * (`target`, `rel`, `class` on links, `type` on ordered lists); this keeps
 * exactly what `checkRichText` allows and drops everything else, including a
 * link with a forbidden scheme.
 */
export function toStoredRichText(json: JSONContent): RichTextDocument {
  // `checkRichText` counts `doc` as level 1, so top-level blocks sit at level 2.
  return { type: 'doc', content: blocks(json.content, 2) };
}

/**
 * Deepest level a list may start at: its list item is one level below, the
 * item's paragraph two and the paragraph's text three.
 */
const MAX_LIST_DEPTH = RICH_TEXT_MAX_DEPTH - 3;

function blocks(content: JSONContent[] | undefined, depth: number): RichTextNode[] {
  const result: RichTextNode[] = [];
  for (const node of Array.isArray(content) ? content : []) {
    if (node.type === 'paragraph') {
      const inline = inlines(node.content);
      result.push(
        inline.length > 0 ? { type: 'paragraph', content: inline } : { type: 'paragraph' },
      );
    } else if (node.type === 'bulletList' || node.type === 'orderedList') {
      if (depth > MAX_LIST_DEPTH) {
        // Too deep to store (e.g. Tab pressed many times, or pasted nested
        // lists): lift the items' content into this level, keeping all text
        // and losing only the extra nesting.
        for (const item of Array.isArray(node.content) ? node.content : []) {
          if (item.type === 'listItem') result.push(...blocks(item.content, depth));
        }
      } else if (node.type === 'bulletList') {
        const items = listItems(node.content, depth + 1);
        if (items.length > 0) result.push({ type: 'bulletList', content: items });
      } else {
        pushOrderedList(result, node, listItems(node.content, depth + 1));
      }
    }
  }
  return result;
}

function pushOrderedList(
  result: RichTextNode[],
  node: JSONContent,
  items: RichTextListItem[],
): void {
  if (items.length === 0) return;
  const start: unknown = node.attrs?.start;
  const keepStart =
    typeof start === 'number' && Number.isInteger(start) && start >= 0 && start !== 1;
  result.push({ type: 'orderedList', ...(keepStart ? { attrs: { start } } : {}), content: items });
}

/** `depth` is the level of the list items themselves. */
function listItems(content: JSONContent[] | undefined, depth: number): RichTextListItem[] {
  return (Array.isArray(content) ? content : [])
    .filter((node) => node.type === 'listItem')
    .map((node) => {
      const inner = blocks(node.content, depth + 1);
      return { type: 'listItem', content: inner.length > 0 ? inner : [{ type: 'paragraph' }] };
    });
}

function inlines(content: JSONContent[] | undefined): RichTextInline[] {
  const result: RichTextInline[] = [];
  for (const node of Array.isArray(content) ? content : []) {
    if (node.type === 'hardBreak') {
      result.push({ type: 'hardBreak' });
    } else if (node.type === 'text' && typeof node.text === 'string' && node.text !== '') {
      const marks = markList(node.marks);
      result.push(
        marks.length > 0
          ? { type: 'text', text: node.text, marks }
          : { type: 'text', text: node.text },
      );
    }
  }
  return result;
}

function markList(marks: JSONContent['marks']): RichTextMark[] {
  const result: RichTextMark[] = [];
  const seen = new Set<string>();
  for (const mark of Array.isArray(marks) ? marks : []) {
    if (seen.has(mark.type)) continue;
    if (mark.type === 'bold' || mark.type === 'italic' || mark.type === 'placeholder') {
      result.push({ type: mark.type });
      seen.add(mark.type);
    } else if (mark.type === 'link') {
      const href: unknown = mark.attrs?.href;
      if (isAllowedHref(href)) {
        result.push({ type: 'link', attrs: { href } });
        seen.add('link');
      }
    }
  }
  return result;
}
